// 근거 보기 데이터: 기록이 저장되면 불러오고, 계산 중이면 잠깐씩 다시 묻는다.
// 글이 바뀐 결과만 다시 묻는다(서버가 바뀐 문장만 다시 잇는다). 서버 글과 화면 글이 다르면 번호를 숨긴다.
import { useCallback, useEffect, useRef, useState } from 'react';
import { getSaenggibuEvidence } from '../../../api/saenggibuApi';
import { hashContent } from './saenggibuEvidence';

const DEBOUNCE_MS = 700;
const FAST_RETRY_MS = 1500;
const FAST_RETRIES = 4;
const PENDING_RETRY_MS = 2500;
const MAX_RETRIES = 30;
const KEYS_PER_REQUEST = 40;

const pickPayload = (evidence, key) => {
  if (!evidence) return null;
  if (evidence.students) {
    const student = evidence.students[key];
    return student ? { sources: student.sources || [], item: student.items?.[key] || null } : null;
  }
  return { sources: evidence.sources || [], item: evidence.items?.[key] || null };
};

/**
 * entries: [{ key, content }] — 단일 v1·v2, 일괄 s0·s1, 학급별 c0v0.
 * 돌려주는 값: { status, get(key, content) → { item, sources } | null, statusOf(key), reload }
 */
export default function useSaenggibuEvidence({ recordId, entries, enabled = true }) {
  const [byKey, setByKey] = useState({});
  const [status, setStatus] = useState('idle');
  const byKeyRef = useRef({});
  byKeyRef.current = byKey;
  const [inFlight, setInFlight] = useState(() => new Set());
  const [reloadTick, setReloadTick] = useState(0);
  const recordRef = useRef(recordId);

  const entriesJson = JSON.stringify((entries || []).filter((entry) => entry?.key && String(entry.content || '').trim()));

  useEffect(() => {
    if (recordRef.current !== recordId) {
      recordRef.current = recordId;
      setByKey({});
      setStatus('idle');
      setInFlight(new Set());
    }
  }, [recordId]);

  useEffect(() => {
    if (!enabled || !recordId) return undefined;
    const list = JSON.parse(entriesJson);
    if (list.length === 0) return undefined;
    let cancelled = false;
    let timer = null;
    let retries = 0;

    const run = async () => {
      const current = byKeyRef.current;
      const stale = list.filter((entry) => current[entry.key]?.content !== entry.content);
      if (stale.length === 0) {
        setStatus((prev) => (prev === 'loading' ? 'ready' : prev));
        return;
      }
      setStatus('loading');
      setInFlight(new Set(stale.map((entry) => entry.key)));
      let again = false;
      let failed = false;
      const updates = {};
      for (let offset = 0; offset < stale.length; offset += KEYS_PER_REQUEST) {
        const chunk = stale.slice(offset, offset + KEYS_PER_REQUEST);
        let evidence = null;
        try {
          const response = await getSaenggibuEvidence(recordId, chunk.map((entry) => entry.key));
          evidence = response?.evidence || null;
        } catch {
          failed = true;
          continue;
        }
        if (cancelled) return;
        if (!evidence || evidence.status === 'off') {
          setStatus('off');
          setInFlight(new Set());
          return;
        }
        if (evidence.status === 'pending') again = true;
        for (const entry of chunk) {
          const payload = pickPayload(evidence, entry.key);
          if (!payload?.item) continue;
          const expected = await hashContent(entry.content);
          if (expected && payload.item.hash && expected !== payload.item.hash) {
            again = true;
            continue;
          }
          updates[entry.key] = { ...payload, content: entry.content };
        }
      }
      if (cancelled) return;
      if (Object.keys(updates).length) setByKey((prev) => ({ ...prev, ...updates }));
      const missing = stale.some((entry) => !updates[entry.key]);
      if (again && missing && retries < MAX_RETRIES) {
        retries += 1;
        timer = setTimeout(run, retries <= FAST_RETRIES ? FAST_RETRY_MS : PENDING_RETRY_MS);
        return;
      }
      setInFlight(new Set());
      setStatus(failed && Object.keys(updates).length === 0 ? 'failed' : 'ready');
    };

    timer = setTimeout(run, DEBOUNCE_MS);
    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  }, [enabled, recordId, entriesJson, reloadTick]);

  const get = useCallback((key, content) => {
    const hit = byKey[key];
    if (!hit || (content !== undefined && hit.content !== content)) return null;
    return hit;
  }, [byKey]);

  const statusOf = useCallback((key, content) => {
    if (!enabled || !recordId) return 'off';
    if (get(key, content)) return 'ready';
    if (status === 'off') return 'off';
    if (status === 'failed') return 'failed';
    if (inFlight.has(key) || status === 'loading' || status === 'idle') return 'loading';
    return 'failed';
  }, [enabled, recordId, get, status, inFlight]);

  const reload = useCallback(() => setReloadTick((tick) => tick + 1), []);

  return { status, get, statusOf, reload };
}
